import { Action, Reactable } from "@reactables/core";
import { RxToggle, RxBuilder } from "@jauntin/reactables";
import { of, concat, timer, from } from "rxjs";
import {
  takeUntil,
  mergeMap,
  concatMap,
  delay,
  filter,
  map,
  share,
  tap,
} from "rxjs/operators";
import CoverageService from "Services/CoverageService";
import { RecentPurchase } from "../Models/recentPurchase.model";

export interface RecentPurchasePopupState {
  optedOut: boolean;
  showPopup: boolean;
  purchase: RecentPurchase;
  count: number;
  recentPurchasesList: RecentPurchase[];
}

interface RecentPurchasePopupActions {
  optOut: () => void;
}
const defaultInitialState: RecentPurchasePopupState = {
  optedOut: false,
  showPopup: false,
  purchase: null,
  count: 0,
  recentPurchasesList: [],
};

const STORAGE_PURCHASE_POPUP_KEY = "recent_purchases_popup_state";

export const RxRecentPurchasePopup = ({
  coverageService,
  timeInterval = 5000,
  showDuration = 8000,
  maxShows = 5,
  storage,
}: {
  storage: {
    getItem: (name: string) => unknown;
    setItem: (name, value) => void;
  };
  coverageService: CoverageService;
  timeInterval?: number;
  showDuration?: number;
  maxShows?: number;
}): Reactable<RecentPurchasePopupState, RecentPurchasePopupActions> => {
  const storedState = storage.getItem(
    STORAGE_PURCHASE_POPUP_KEY,
  ) as RecentPurchasePopupState;

  const [optOut$, { toggleOn: optOut }] = RxToggle(
    Boolean(storedState?.optedOut),
  );

  const optedOut$ = optOut$.pipe(
    filter((optOut) => optOut),
    map(() => ({ type: "optedOut" })),
  );

  const recentPurchases$ = (
    storedState?.recentPurchasesList
      ? of(storedState.recentPurchasesList)
      : from(coverageService.getRecentPurchases()).pipe(
          map(({ data }: { data: RecentPurchase[] }) => data),
        )
  ).pipe(share());

  const purchases$ = from(recentPurchases$).pipe(
    mergeMap((data) =>
      of(
        ...data.slice(
          storedState?.count || defaultInitialState.count,
          maxShows,
        ),
      ),
    ),
    concatMap((purchase) =>
      timer(timeInterval).pipe(
        mergeMap(() =>
          concat(
            of({ type: "showPurchase", payload: purchase }),
            of({ type: "hidePopup" }).pipe(delay(showDuration)),
          ),
        ),
      ),
    ),
    takeUntil(optedOut$),
  );

  const rxRecentPurchasePopup = RxBuilder({
    sources: [
      purchases$,
      optedOut$,
      recentPurchases$.pipe(
        map((purchases) => ({
          type: "recentPurchasesReceived",
          payload: purchases,
        })),
      ),
    ],
    initialState: storedState
      ? { ...storedState, showPopup: false }
      : defaultInitialState,
    name: "rxRecentPurchasePopup",
    reducers: {
      showPurchase: (state, { payload }) => ({
        ...state,
        showPopup: true,
        count: state.count + 1,
        purchase: payload as RecentPurchase,
      }),
      hidePopup: (state) => ({ ...state, showPopup: false }),
      optedOut: (state) => ({ ...state, showPopup: false, optedOut: true }),
      recentPurchasesReceived: (
        state,
        { payload }: Action<RecentPurchase[]>,
      ) => ({
        ...state,
        recentPurchasesList: payload,
      }),
    },
  });

  const state$ = rxRecentPurchasePopup[0].pipe(
    tap((state) => {
      storage.setItem(STORAGE_PURCHASE_POPUP_KEY, state);
    }),
  );

  const actions = { optOut };

  return [state$, actions];
};
